import { useState, useEffect } from 'react';
import { Send, Megaphone, Loader2, Store, Clock, Power } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function PushCampaignsPage() {
  const [branches, setBranches] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(null);
  const [form, setForm] = useState({ title: '', body: '', url: '', branch_id: '' });

  useEffect(() => {
    api.get('/sucursales').then(r => setBranches(r.data.sucursales || [])).catch(() => {});
    api.get('/admin/push-campaigns').then(r => setCampaigns(r.data.campaigns || [])).catch(() => {});
  }, []);

  const send = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error('Título y mensaje son obligatorios.');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        url: form.url.trim() || null,
        branch_id: form.branch_id ? parseInt(form.branch_id) : null,
      };
      const { data } = await api.post('/admin/push-campaigns/send', payload);
      toast.success(data.mensaje);
      setCampaigns(prev => [data.campaign, ...prev]);
      setForm({ title: '', body: '', url: '', branch_id: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al enviar campaña.');
    }
    setLoading(false);
  };

  const toggleActive = async (id, currentActive) => {
    setToggling(id);
    try {
      const { data } = await api.patch(`/admin/push-campaigns/${id}/active`, { active: !currentActive });
      toast.success(data.mensaje);
      setCampaigns(prev => prev.map(c => c.id === id ? { ...c, active: data.campaign.active } : c));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al cambiar estado.');
    }
    setToggling(null);
  };

  return (
    <div data-testid="push-campaigns-page" className="space-y-6">
      <div>
        <h2 className="text-lg font-bold flex items-center gap-2">
          <Megaphone size={20} className="text-[#E84C1C]" /> Push de Promociones
        </h2>
        <p className="text-sm text-zinc-500 mt-1">Enviá notificaciones push a clientes que activaron promociones.</p>
      </div>

      {/* Form */}
      <div className="bg-zinc-900 border border-zinc-800/50 rounded-xl p-4 space-y-3">
        <div>
          <label className="text-xs text-zinc-400 mb-1 block">Título *</label>
          <input
            data-testid="campaign-title"
            value={form.title}
            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            placeholder="Ej: Promo del día"
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:border-[#E84C1C]/50 focus:outline-none"
          />
        </div>
        <div>
          <label className="text-xs text-zinc-400 mb-1 block">Mensaje *</label>
          <textarea
            data-testid="campaign-body"
            value={form.body}
            onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
            placeholder="Ej: 20% off en todos los rolls hoy"
            rows={2}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:border-[#E84C1C]/50 focus:outline-none resize-none"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Sucursal</label>
            <select
              data-testid="campaign-branch"
              value={form.branch_id}
              onChange={e => setForm(p => ({ ...p, branch_id: e.target.value }))}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm focus:border-[#E84C1C]/50 focus:outline-none"
            >
              <option value="">Todas las sucursales</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">URL destino (opcional)</label>
            <input
              data-testid="campaign-url"
              value={form.url}
              onChange={e => setForm(p => ({ ...p, url: e.target.value }))}
              placeholder="Ej: /menu"
              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:border-[#E84C1C]/50 focus:outline-none"
            />
          </div>
        </div>
        <button
          data-testid="campaign-send-btn"
          onClick={send}
          disabled={loading || !form.title.trim() || !form.body.trim()}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-[#E84C1C] text-white font-semibold hover:bg-[#d43f12] active:scale-[0.98] transition-all disabled:opacity-50"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          {loading ? 'Enviando...' : 'Enviar ahora'}
        </button>
      </div>

      {/* Campaign history */}
      <div>
        <h3 className="text-sm font-semibold text-zinc-300 mb-3">Historial de campañas</h3>
        {campaigns.length === 0 ? (
          <p className="text-xs text-zinc-500">No hay campañas enviadas aún.</p>
        ) : (
          <div className="space-y-2">
            {campaigns.map(c => (
              <div
                key={c.id}
                data-testid={`campaign-${c.id}`}
                className={`p-3 bg-zinc-900 border rounded-xl transition-colors ${c.active === false ? 'border-zinc-800/30 opacity-60' : 'border-zinc-800/50'}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{c.title}</p>
                    <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{c.body}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs font-bold text-[#E84C1C] bg-[#E84C1C]/10 px-2 py-1 rounded-lg">
                      {c.sent_count} env.
                    </span>
                    <button
                      data-testid={`campaign-toggle-${c.id}`}
                      onClick={() => toggleActive(c.id, c.active !== false)}
                      disabled={toggling === c.id}
                      className={`p-1.5 rounded-lg transition-colors ${
                        c.active === false
                          ? 'bg-zinc-800 text-zinc-500 hover:text-zinc-300'
                          : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                      }`}
                      title={c.active === false ? 'Activar campaña' : 'Desactivar campaña'}
                    >
                      <Power size={14} />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2 text-[10px] text-zinc-500">
                  <span className="flex items-center gap-1"><Store size={10} /> {c.branch_name || 'Todas'}</span>
                  <span className="flex items-center gap-1"><Clock size={10} /> {new Date(c.sent_at || c.created_at).toLocaleString('es-PY')}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                    c.active === false ? 'bg-red-500/10 text-red-400' : 'bg-emerald-500/10 text-emerald-400'
                  }`}>
                    {c.active === false ? 'Desactivada' : 'Activa'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
